import { useState } from 'react';
import {
  CheckIcon,
  CloseIcon,
  LinkIcon,
  MinimizeIcon,
  SpinnerIcon
} from './icons';
import { UNHANDLED_TOOL_ERROR } from './tools/assistantToolDispatch';
import {
  blendToWhite,
  DEFAULT_CHAT_COLOR,
  GRAY_200,
  GRAY_400,
  GRAY_500,
  RED_500
} from './colors';

// The indicator the pinned status strip holds. Its metrics live with the
// component that draws it so the strip's reserved height, exported below,
// cannot drift from the line it has to fit
const STATUS_LABEL_FONT_SIZE = 17;
const STATUS_LABEL_LINE_HEIGHT = 24;
const STATUS_LABEL_PADDING_Y = 4;
export const STATUS_LABEL_BLOCK_HEIGHT =
  STATUS_LABEL_LINE_HEIGHT + STATUS_LABEL_PADDING_Y * 2;

export interface ToolLabel {
  running: string;
  done?: string;
}

export const TOOL_LABELS: Record<string, ToolLabel> = {
  searchWeb: {
    running: 'Searching the web...',
    done: 'Searched the web'
  },
  scrapeUrl: { running: 'Reading web page...', done: 'Read the web page' },
  searchDocuments: {
    running: 'Searching documents...',
    done: 'Searched the documents'
  },
  setFieldValue: {
    running: 'Updating form fields...',
    done: 'Updated form fields'
  },
  clickElement: {
    running: 'Running form action...',
    done: 'Ran the form action'
  },
  navigateToStep: {
    running: 'Moving to another page...',
    done: 'Moved to another page'
  },
  addTableRow: {
    running: 'Adding a row...',
    done: 'Added a row'
  },
  deleteTableRow: {
    running: 'Deleting a row...',
    done: 'Deleted a row'
  },
  setTableCellValue: {
    running: 'Updating table cells...',
    done: 'Updated table cells'
  },
  triggerTableAction: {
    running: 'Running table action...',
    done: 'Ran the table action'
  },
  queryAttachmentSemantic: {
    running: 'Reading attachments...',
    done: 'Read the attachments'
  },
  getPanelSnapshot: {
    running: 'Reading the form...',
    done: 'Reviewed the form'
  },
  getExtractionSnapshot: {
    running: 'Reading extraction setup...',
    done: 'Reviewed the extraction setup'
  },
  getExtractionResults: {
    running: 'Reading extraction results...',
    done: 'Reviewed the extraction results'
  },
  getLogicRules: {
    running: 'Reading form logic...',
    done: 'Reviewed form logic'
  },
  getHubSchema: {
    running: 'Reading record setup...',
    done: 'Reviewed the record setup'
  },
  queryHub: {
    running: 'Searching records on file...',
    done: 'Searched the records on file'
  },
  querySubmissions: {
    running: 'Searching submissions...',
    done: 'Searched submissions'
  }
};

export function readPartType(
  part: any
): { kind: 'text' | 'tool'; toolName?: string } | null {
  if (part?.type === 'text') return { kind: 'text' };
  const isStatic =
    typeof part?.type === 'string' && part.type.startsWith('tool-');
  const isDynamic = part?.type === 'dynamic-tool';
  if (!isStatic && !isDynamic) return null;
  const toolName = isStatic
    ? part.type.replace('tool-', '')
    : (part.toolName as string) || 'unknown';
  return { kind: 'tool', toolName };
}

// Format URL for display (show domain + path); CSS handles truncation.
const formatUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return parsed.hostname + (parsed.pathname !== '/' ? parsed.pathname : '');
  } catch {
    return url;
  }
};

const extractUrls = (output: unknown): string[] => {
  if (!output || typeof output !== 'object') return [];
  const out = output as Record<string, unknown>;
  const urls: string[] = [];
  const extractFromArray = (arr: unknown[]): string[] =>
    arr
      .map((r: unknown) => {
        if (typeof r === 'object' && r !== null) {
          const item = r as Record<string, unknown>;
          return (item.url as string) || '';
        }
        return '';
      })
      .filter(Boolean);
  if (Array.isArray(out.web)) urls.push(...extractFromArray(out.web));
  if (Array.isArray(out.news)) urls.push(...extractFromArray(out.news));
  if (Array.isArray(out.images)) urls.push(...extractFromArray(out.images));
  if (typeof out.url === 'string' && out.url) urls.push(out.url);
  if (urls.length > 0) return Array.from(new Set(urls));
  if (Array.isArray(output))
    return Array.from(new Set(extractFromArray(output)));
  return [];
};

export interface ToolRow {
  key: string;
  toolName: string;
  state: string;
  input?: { query?: string };
  output?: unknown;
}

const isRunningState = (s: string) =>
  s === 'input-streaming' || s === 'input-available';
const isPlaceholder = (row: ToolRow) =>
  typeof row.output === 'object' &&
  row.output !== null &&
  (row.output as { error?: unknown }).error === UNHANDLED_TOOL_ERROR;
const isErrorRow = (row: ToolRow) =>
  row.state === 'output-error' ||
  (typeof row.output === 'object' &&
    row.output !== null &&
    (row.output as { ok?: unknown }).ok === false);

interface ToolRowDetailProps {
  row: ToolRow;
  linkColor: string;
  indent?: number;
}

const ToolRowDetail = ({ row, linkColor, indent = 24 }: ToolRowDetailProps) => {
  const query = row.input?.query;
  const urls = extractUrls(row.output);
  const hasContent = !!query || urls.length > 0;
  if (!hasContent) return null;
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        paddingLeft: `${indent}px`,
        marginTop: '2px',
        marginBottom: '4px'
      }}
    >
      {query && (
        <div
          css={{
            color: GRAY_400,
            fontSize: '12px',
            fontStyle: 'italic'
          }}
        >
          &quot;{query.length > 80 ? query.substring(0, 80) + '...' : query}
          &quot;
        </div>
      )}
      {urls.map((url, i) => (
        <a
          key={i}
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          css={{
            fontSize: '12px',
            color: linkColor,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minWidth: 0,
            ':hover': { textDecoration: 'underline' }
          }}
        >
          <LinkIcon css={{ flexShrink: 0 }} />
          <span
            css={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {formatUrl(url)}
          </span>
        </a>
      ))}
    </div>
  );
};

const hasDetail = (row: ToolRow): boolean => {
  if (row.input?.query) return true;
  if (extractUrls(row.output).length > 0) return true;
  return false;
};

const labelFor = (row: ToolRow, pending = false): string => {
  const labels = TOOL_LABELS[row.toolName] || {
    running: 'Working...',
    done: 'Done'
  };
  if (pending || isRunningState(row.state)) return labels.running;
  if (isErrorRow(row)) return labels.done ?? 'Failed';
  return labels.done ?? labels.running;
};

// background-clip:text so the gradient sweep only paints the glyphs
// The travelling highlight is derived from the base so any accent, not just the
// default gray, shimmers with the same contrast
const shimmerCss = (base: string = GRAY_500) => ({
  backgroundImage: `linear-gradient(90deg, ${base} 25%, ${blendToWhite(
    base,
    70
  )} 50%, ${base} 75%)`,
  backgroundSize: '220% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
  animation: 'feathery-tool-shimmer 1.6s linear infinite',
  '@keyframes feathery-tool-shimmer': {
    from: { backgroundPosition: '160% 0' },
    to: { backgroundPosition: '-60% 0' }
  }
});

// One even wash across the whole region. A uniform multiply barely moves a
// contrast ratio - measured off the rendered pixels, the live indicator's
// default gray goes from 4.83:1 to 4.57:1 under this, still past AA - so it
// stays legible and the strip needs no lighter treatment of its own. Washing
// the strip more lightly was tried and read as a pale band with an edge across
// the panel, which is worse to look at than the even dim
const WASH = 'rgba(0, 0, 0, 0.1)';

interface ToolChunkProps {
  rows: ToolRow[];
  followedByText: boolean;
  turnFinished: boolean;
  audioPending?: boolean;
  linkColor?: string;
  isFirstChunk?: boolean;
}

export const ToolChunk = ({
  rows,
  followedByText,
  turnFinished,
  audioPending = false,
  linkColor = DEFAULT_CHAT_COLOR,
  isFirstChunk = false
}: ToolChunkProps) => {
  const isRunning = rows.some(
    (r) => isRunningState(r.state) || (!turnFinished && isPlaceholder(r))
  );
  // Voice: keep showing "Working on it" until the following reply's audio arrives
  const chunkDone =
    !isRunning && !audioPending && (followedByText || turnFinished);
  const shouldCollapse = !isRunning && !audioPending && followedByText;
  const defaultExpanded = !shouldCollapse;
  const [override, setOverride] = useState<boolean | null>(null);
  const expanded = override ?? defaultExpanded;
  const toggle = () => setOverride(!expanded);

  // Match the message bubble's horizontal padding
  const TEXT_ALIGN_PADDING = 14;

  const containerCss = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    fontSize: '13px',
    minWidth: 0,
    paddingLeft: `${TEXT_ALIGN_PADDING}px`,
    ...(isFirstChunk
      ? {}
      : {
          animation: 'feathery-chunk-fade-in 220ms ease-out both',
          '@keyframes feathery-chunk-fade-in': {
            from: { opacity: 0, transform: 'translateY(4px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          }
        })
  };

  // Tools without a label aren't shown individually, only the chunk header
  const labeledRows = rows.filter((row) => row.toolName in TOOL_LABELS);

  // Inline only when done; wrapping while in-flight avoids reflow on a 2nd row
  if (chunkDone && labeledRows.length === 1) {
    const row = labeledRows[0];
    const error = isErrorRow(row);
    const expandable = hasDetail(row);
    return (
      <div css={containerCss}>
        <button
          type='button'
          onClick={expandable ? toggle : undefined}
          css={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            padding: '4px 0',
            cursor: expandable ? 'pointer' : 'default',
            color: GRAY_500,
            textAlign: 'left',
            font: 'inherit',
            fontSize: '13px',
            alignSelf: 'flex-start'
          }}
        >
          {error && (
            <CloseIcon
              css={{ width: '12px', height: '12px', color: RED_500 }}
            />
          )}
          <span>{labelFor(row)}</span>
          {expandable && (
            <MinimizeIcon
              css={{
                width: '12px',
                height: '12px',
                color: GRAY_400,
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
                flexShrink: 0
              }}
            />
          )}
        </button>
        {expandable && expanded && (
          <ToolRowDetail row={row} linkColor={linkColor} indent={6} />
        )}
      </div>
    );
  }

  // While the turn runs, the pinned status strip is the only place the
  // "Working on it..." indicator belongs. A copy here scrolled away with the
  // transcript, so show just the tools themselves until the chunk finishes -
  // and nothing at all when none of them has a label to show.
  if (!chunkDone) {
    if (labeledRows.length === 0) return null;
    return (
      <div css={containerCss}>
        {labeledRows.map((row) => (
          <ToolChunkRow
            key={row.key}
            row={row}
            pending={!turnFinished && isPlaceholder(row)}
            linkColor={linkColor}
          />
        ))}
      </div>
    );
  }

  // Header path: the summary always shows; the list expands only when
  // there are displayable tools behind it
  const expandable = labeledRows.length > 0;

  return (
    <div css={containerCss}>
      {/* Header: label + chevron, no spinner/check. Chevron sits inline
          immediately after the label as the expand/collapse affordance. */}
      <button
        type='button'
        onClick={expandable ? toggle : undefined}
        css={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          padding: '4px 0',
          cursor: expandable ? 'pointer' : 'default',
          color: GRAY_500,
          textAlign: 'left',
          font: 'inherit',
          fontSize: '13px',
          alignSelf: 'flex-start'
        }}
      >
        <span>Finished working</span>
        {expandable && (
          <MinimizeIcon
            css={{
              width: '12px',
              height: '12px',
              color: GRAY_400,
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s ease',
              flexShrink: 0
            }}
          />
        )}
      </button>
      {expandable && expanded && (
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            marginLeft: '5px',
            paddingLeft: '13px',
            borderLeft: `2px solid ${GRAY_200}`
          }}
        >
          {labeledRows.map((row) => (
            <ToolChunkRow
              key={row.key}
              row={row}
              pending={!turnFinished && isPlaceholder(row)}
              linkColor={linkColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface ToolChunkRowProps {
  row: ToolRow;
  pending: boolean;
  linkColor: string;
}

const ToolChunkRow = ({ row, pending, linkColor }: ToolChunkRowProps) => {
  const expandable = hasDetail(row);
  const running = pending || isRunningState(row.state);
  const error = !running && isErrorRow(row);
  // Auto-expanded while running, collapses when output lands
  const [override, setOverride] = useState<boolean | null>(null);
  const expanded = override ?? running;
  const setExpanded = (next: boolean) => setOverride(next);
  const RowEl = expandable ? 'button' : 'div';
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <RowEl
        type={expandable ? 'button' : undefined}
        onClick={expandable ? () => setExpanded(!expanded) : undefined}
        css={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: error ? RED_500 : GRAY_500,
          background: 'none',
          border: 'none',
          padding: '2px 0',
          textAlign: 'left',
          cursor: expandable ? 'pointer' : 'default',
          font: 'inherit',
          fontSize: '13px',
          alignSelf: 'flex-start'
        }}
      >
        {running ? (
          <SpinnerIcon css={{ width: '12px', height: '12px' }} />
        ) : error ? (
          <CloseIcon css={{ width: '12px', height: '12px' }} />
        ) : (
          <CheckIcon css={{ width: '12px', height: '12px' }} />
        )}
        <span css={running ? shimmerCss() : undefined}>
          {labelFor(row, pending)}
        </span>
        {expandable && (
          <MinimizeIcon
            css={{
              width: '10px',
              height: '10px',
              color: GRAY_400,
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s ease',
              flexShrink: 0
            }}
          />
        )}
      </RowEl>
      {expandable && expanded && (
        <ToolRowDetail row={row} linkColor={linkColor} />
      )}
    </div>
  );
};

// Mimics the chunk header so a real tool arriving doesn't reflow the layout
export const ToolChunkPlaceholder = ({
  label = 'Working on it...',
  color = GRAY_500
}: {
  label?: string;
  color?: string;
}) => {
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        fontSize: `${STATUS_LABEL_FONT_SIZE}px`,
        // A flex item defaults to min-width:auto, which floors it at the
        // label's full width - the nowrap label below would then overflow the
        // panel instead of clipping. Both of these are load-bearing
        flex: '0 1 auto',
        minWidth: 0,
        paddingLeft: '14px',
        animation: 'feathery-chunk-fade-in 220ms ease-out both',
        '@keyframes feathery-chunk-fade-in': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      <div
        css={{
          padding: `${STATUS_LABEL_PADDING_Y}px 0`,
          color,
          fontSize: `${STATUS_LABEL_FONT_SIZE}px`,
          // Stretched rather than flex-start so the box is the strip's width,
          // which is what the ellipsis clips against once the panel is narrow
          alignSelf: 'stretch',
          // The label can swap for a longer one while running, so hold it to a
          // single line: nothing below the indicator may move when it changes,
          // and a narrow panel truncates the phrase rather than wrapping it
          maxWidth: '100%',
          minWidth: 0,
          lineHeight: `${STATUS_LABEL_LINE_HEIGHT}px`,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        <span
          // Remounting on a new label replays the fade, so phrases cross over.
          // Opacity only - the wrapper's fade-in also shifts position, which
          // would read as the phrase sliding rather than changing
          key={label}
          css={{
            ...shimmerCss(color),
            animation:
              'feathery-phrase-fade 220ms ease-out both, feathery-tool-shimmer 1.6s linear infinite',
            '@keyframes feathery-phrase-fade': {
              from: { opacity: 0 },
              to: { opacity: 1 }
            },
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              backgroundImage: 'none',
              color
            }
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
};

// Dims the region a turn is happening in - the transcript plus the status
// strip - so the panel as a whole reads as busy, not just the small live label
// inside it. Purely a signal: it never takes pointer events, so the transcript
// underneath stays scrollable, selectable and clickable throughout the turn.
export const BusyWash = () => (
  <div
    aria-hidden
    css={{
      position: 'absolute',
      inset: 0,
      // Deliberately no z-index: the thread dropdown and the menu backdrops
      // carry their own, so they keep painting above this
      pointerEvents: 'none',
      overflow: 'hidden',
      backgroundColor: WASH,
      // Held back so a turn that finishes inside a few hundred milliseconds
      // never flashes a wash on and straight off again. Fill mode `both` keeps
      // it fully transparent for the whole delay, not just faded
      animation: 'feathery-busy-wash-in 160ms ease-out 200ms both',
      '@keyframes feathery-busy-wash-in': {
        from: { opacity: 0 },
        to: { opacity: 1 }
      }
    }}
  >
    {/* A wide, low-contrast highlight crossing the region. Slow on purpose:
        this sits beside a document being read, so it has to be noticeable
        without competing for attention */}
    <div
      css={{
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'linear-gradient(100deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 70%)',
        animation: 'feathery-busy-shimmer 3.2s linear infinite',
        // Off the left edge to off the right one, so the sweep enters and
        // leaves rather than jumping back at the wrap
        '@keyframes feathery-busy-shimmer': {
          from: { transform: 'translateX(-100%)' },
          to: { transform: 'translateX(100%)' }
        },
        // A travelling animation that runs for the whole turn is exactly what
        // motion sensitivity suffers from. The static dim carries the signal
        '@media (prefers-reduced-motion: reduce)': { display: 'none' }
      }}
    />
  </div>
);

export default ToolChunk;
